import { useEffect } from 'react';
import { environment } from 'environment';

/**
 * Frontend auto-refresh.
 *
 * Polls the backend for the minimum acceptable frontend version and reloads a
 * stale session onto the new build, with a hard guarantee against reload loops.
 *
 * Guard (per session, per received minimum version):
 *  - running >= minimum            -> clear the flag, do nothing.
 *  - running <  minimum, not tried -> store the flag, reload.
 *  - running <  minimum, tried     -> do nothing (a bad deploy can't trap you).
 *
 * A failed check (network/backend down) never triggers a reload.
 */

const REFRESH_FLAG_KEY = 'pc.frontendRefreshTarget';
const POLL_INTERVAL_MS = 30_000;
const VERSION_PATH = '/minimum-client-version';

/**
 * Compares two dotted-numeric versions using version-precedence ordering
 * (so 1.0.10 > 1.0.9). Returns a negative number if a < b, 0 if equal, and a
 * positive number if a > b.
 */
export const compareVersions = (a: string, b: string): number => {
  const pa = a.split('.').map(part => parseInt(part, 10) || 0);
  const pb = b.split('.').map(part => parseInt(part, 10) || 0);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) {
      return diff;
    }
  }

  return 0;
};

const evaluate = (running: string, minimum: string): void => {
  const flag = localStorage.getItem(REFRESH_FLAG_KEY);

  // Up to date: clear any prior attempt and stop.
  if (compareVersions(running, minimum) >= 0) {
    if (flag !== null) {
      localStorage.removeItem(REFRESH_FLAG_KEY);
    }
    return;
  }

  // Stale, but we already tried to reach this exact target -> do not loop.
  if (flag === minimum) {
    return;
  }

  // Stale, not yet attempted -> record the target, then reload.
  localStorage.setItem(REFRESH_FLAG_KEY, minimum);
  window.location.reload();
};

const check = async (): Promise<void> => {
  try {
    const response = await fetch(`${environment.apiRoute}${VERSION_PATH}`, {
      method: 'GET',
      cache: 'no-store',
    });

    // A failed check must never trigger a reload.
    if (!response.ok) {
      return;
    }

    const minimum = (await response.text()).trim();
    if (minimum) {
      evaluate(environment.version, minimum);
    }
  } catch {
    // Network error / backend down: take no action this cycle.
  }
};

/** Runs the version check on mount and then on a fixed interval. */
export const useFrontendRefresh = (): void => {
  useEffect(() => {
    check();
    const id = window.setInterval(check, POLL_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, []);
};
